import { useEffect, useMemo, useRef, useState } from "react";
import { mediaSrc } from "@/lib/media";

export const GuidePhotoPositioner = ({ file, existingUrl, value, onChange, testid }) => {
  const previewRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(() => () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  const src = objectUrl || (existingUrl ? mediaSrc(existingUrl) : "");
  const x = value?.photo_position_x ?? 50;
  const y = value?.photo_position_y ?? 50;
  const zoom = value?.photo_zoom ?? 1;

  if (!src) return null;

  const updateFromPoint = (clientX, clientY) => {
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextX = Math.round(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
    const nextY = Math.round(Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100)));
    onChange({ photo_position_x: nextX, photo_position_y: nextY, photo_zoom: zoom });
  };

  return (
    <div data-testid={testid} className="sm:col-span-2 rounded-2xl border border-border bg-white/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Crop / reposition photo</p>
        <span data-testid={`${testid}-position-label`} className="text-xs text-muted-foreground">Focus {x}% · {y}%</span>
      </div>
      <div
        ref={previewRef}
        data-testid={`${testid}-preview`}
        onPointerDown={(e) => {
          previewRef.current?.setPointerCapture?.(e.pointerId);
          setDragging(true);
          updateFromPoint(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => dragging && updateFromPoint(e.clientX, e.clientY)}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        className="relative mx-auto aspect-[4/5] w-full max-w-[240px] cursor-crosshair overflow-hidden rounded-2xl bg-muted touch-none select-none"
      >
        <img
          src={src}
          alt="Guide crop preview"
          draggable={false}
          data-testid={`${testid}-image`}
          className="h-full w-full object-cover transition-transform duration-200"
          style={{ objectPosition: `${x}% ${y}%`, transform: `scale(${zoom})`, transformOrigin: `${x}% ${y}%` }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-black/20 shadow"
          style={{ left: `${x}%`, top: `${y}%` }}
        />
      </div>
      <label className="mt-4 block text-xs uppercase tracking-widest text-muted-foreground" htmlFor={`${testid}-zoom`}>Zoom</label>
      <div className="mt-2 flex items-center gap-3">
        <input
          id={`${testid}-zoom`}
          type="range"
          min="1"
          max="2"
          step="0.05"
          value={zoom}
          data-testid={`${testid}-zoom`}
          onChange={(e) => onChange({ photo_position_x: x, photo_position_y: y, photo_zoom: Number(e.target.value) })}
          className="w-full accent-[#1A2E26]"
        />
        <span data-testid={`${testid}-zoom-label`} className="w-12 text-right text-xs text-muted-foreground">{Math.round(zoom * 100)}%</span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Drag or tap the preview to choose what stays visible in the public 4:5 photo card.</p>
    </div>
  );
};
